import {
    EuiButton,
    EuiButtonEmpty, EuiCheckbox,
    EuiFlexGroup,
    EuiFlexItem,
    EuiFormRow,
    EuiSpacer,
    EuiTitle,
    EuiFieldNumber
} from "@elastic/eui";
import {useNavigate, useParams} from "react-router-dom";
import React, {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {RootState} from "../../slices/RootReducer";
import {useGetProductGroupsQuery} from "../../api/rtkQueryApi/ProductsApi";
import {Product, ProductDetails} from "../../api/entities/Product";
import {ProductGroup} from "../../api/entities/ProductGroup";
import {setBusinessBuilderAction} from "../../slices/NewOrderCreateSlice";
import {ProductDetailsCard} from "../../components/products/ProductDetailsCard";
import {ProductSelector} from "../product/ProductSelector";
import {FieldPercentage} from "../../components/fields/FieldPercentage";
import {GetProductDetails} from "utils/PlanUtils";

export const NewOrderProduct: React.FC = () => {
    const {businessBuilder} = useSelector((state: RootState) => state.newOrder);
    const {businessId} = useParams();
    const navigate = useNavigate();
    const getProductGroupsQuery = useGetProductGroupsQuery();
    const [productGroups, setProductGroups] = useState<ProductGroup[]>([]);
    const [selectedProduct, setSelectedProduct] = useState<Product>();
    const [selectedProductGroup, setSelectedProductGroup] =
        useState<ProductGroup>();
    const dispatch = useDispatch();
    const [discount, setDiscount] = useState<number>(0);
    const [showAllProducts, setShowAllProducts] = useState(false);
    const [selectedProductDetails, setSelectedProductDetails] =
        useState<ProductDetails>();

    useEffect(() => {
        setSelectedProductDetails(
            GetProductDetails(selectedProduct, selectedProductGroup),
        );
    }, [selectedProduct, selectedProductGroup]);

    useEffect(() => {
        if (getProductGroupsQuery.data) {
            setProductGroups(getProductGroupsQuery.data);
            if (businessBuilder.productId) {
                setSelectedProduct(
                    getProductGroupsQuery.data
                        .flatMap((group) => group.products)
                        .find((product) => product.id === businessBuilder.productId),
                );
                setSelectedProductGroup(
                    getProductGroupsQuery.data.find((group) =>
                        group.products.find(
                            (product) => product.id === businessBuilder.productId,
                        ),
                    ),
                );
            }
        }
    }, [getProductGroupsQuery.data, businessBuilder.productId]);

    const onBackClick = () => {
        const selectedProductGroup = productGroups.find((group) =>
            group.products.find((product) => product.id === selectedProduct?.id),
        );
        dispatch(
            setBusinessBuilderAction({
                ...businessBuilder,
                productId: selectedProduct?.id,
                product: selectedProductDetails,
                productGroupId: selectedProductGroup?.id,
                discount: discount,
                productGroup: selectedProductGroup,
            }),
        );

        if (businessId) {
            navigate("/business/" + businessId);
        } else {
            navigate("/new");
        }
    };

    const onContinueClick = () => {
        const selectedProductGroup = productGroups.find((group) =>
            group.products.find((product) => product.id === selectedProduct?.id),
        );
        dispatch(
            setBusinessBuilderAction({
                ...businessBuilder,
                productId: selectedProduct?.id,
                product: selectedProductDetails,
                productGroupId: selectedProductGroup?.id,
                discount: discount,
                productGroup: selectedProductGroup,
            }),
        );

        if (businessId) {
            navigate(`/business/${businessId}/new/addons`);
        } else {
            navigate(`/new/addons`);
        }
    };

    const onProductSelected = (product: Product) => {
        setSelectedProduct(product);
        const productGroup = productGroups.find((group) =>
            group.products.find((p) => p.id === product?.id),
        );
        setSelectedProductGroup(productGroup);
        setDiscount(0);
        scrollToTop();
    };

    const scrollToTop = () => {
        const bodyContent = document.getElementById("bodyContentId");
        if (bodyContent) {
            bodyContent.scrollTo({top: 0, behavior: "smooth"});
        }
    }

    return (
        <>
            <EuiFormRow>
                <EuiFlexGroup>
                    <EuiFlexItem grow={false}>
                        <EuiButton onClick={onBackClick}>Back</EuiButton>
                    </EuiFlexItem>
                    <EuiFlexItem grow={false}>
                        <EuiButton
                            fill
                            onClick={onContinueClick}
                            disabled={!selectedProduct?.id}
                        >
                            Continue
                        </EuiButton>
                    </EuiFlexItem>
                </EuiFlexGroup>
            </EuiFormRow>
            {!selectedProductDetails && <EuiSpacer/>}
            {selectedProductDetails && (
                <>
                    {selectedProductDetails.maxDiscount > 0 && (
                        <>
                            <EuiFormRow label={"Authorized Discount"}>
                                <FieldPercentage
                                    value={discount}
                                    onChange={(val) => setDiscount(val)}
                                    max={selectedProductDetails.maxDiscount}
                                />
                            </EuiFormRow>
                            <EuiSpacer/>
                        </>
                    )}

                    <EuiFormRow>
                        <EuiCheckbox checked={businessBuilder.isOverrideSetupFee} onChange={() => {
                            dispatch(
                                setBusinessBuilderAction({
                                    ...businessBuilder,
                                    isOverrideSetupFee: !businessBuilder.isOverrideSetupFee,
                                }),
                            );
                        }} id={'waveSetupFeeCB'} label={"Override Setup Fee"} />
                    </EuiFormRow>
                    {businessBuilder.isOverrideSetupFee && (
                        <EuiFormRow label={"New Setup Fee"}>
                            <EuiFieldNumber prepend={'$'} value={businessBuilder.overrideSetupFee ?? 0} min={0} onChange={e => {
                                dispatch(setBusinessBuilderAction({
                                    ...businessBuilder,
                                    overrideSetupFee: Number(e.target.value),
                                }))
                            }}/>
                        </EuiFormRow>
                    )}
                    <EuiSpacer/>
                    <EuiFlexGroup>
                        <EuiFlexItem grow={false}>
                            <ProductDetailsCard
                                productDetails={selectedProductDetails}
                                discount={discount}
                                isOverrideSetupFee={businessBuilder.isOverrideSetupFee}
                                overrideSetupFee={businessBuilder.overrideSetupFee ?? 0}
                            />
                        </EuiFlexItem>
                    </EuiFlexGroup>
                    <EuiSpacer/>
                </>
            )}
            <EuiFlexGroup>
                <EuiFlexItem grow={false}>
                    <EuiTitle size={"s"}>
                        <h2>Available Products</h2>
                    </EuiTitle>
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                    <EuiButtonEmpty onClick={() => setShowAllProducts((prev) => !prev)}>
                        Show All Products
                    </EuiButtonEmpty>
                </EuiFlexItem>
            </EuiFlexGroup>
            <EuiSpacer size={"m"}/>
            <ProductSelector
                selectedProductId={selectedProduct?.id}
                onProductSelected={onProductSelected}
                center={true}
                showAllProducts={showAllProducts}
            />
        </>
    );
};
